import type { ATSJob } from '@kiku/integrations/types';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText
} from '~/components/ui/input-group';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from '~/components/ui/pagination';
import { Skeleton } from '~/components/ui/skeleton';

const PAGE_SIZE = 20;

function getPageRange(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | 'ellipsis')[] = [];
  const addPage = (p: number): void => {
    if (pages[pages.length - 1] !== p) pages.push(p);
  };
  addPage(1);
  if (current > 3) pages.push('ellipsis');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) addPage(p);
  if (current < total - 2) pages.push('ellipsis');
  addPage(total);
  return pages;
}

type AtsSelectorStepProps = {
  atsJobs: ATSJob[] | undefined;
  isLoading: boolean;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onContinue: () => void;
  singleSelect?: boolean;
};

export function AtsSelectorStep({
  atsJobs,
  isLoading,
  selectedIds,
  onSelectionChange,
  onContinue,
  singleSelect = false
}: AtsSelectorStepProps): React.ReactNode {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const filtered = atsJobs?.filter((j) => j.title.toLowerCase().includes(search.toLowerCase()));
  const totalPages = Math.ceil((filtered?.length ?? 0) / PAGE_SIZE);
  const paginated = filtered?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function toggleJob(id: string): void {
    if (singleSelect) {
      onSelectionChange(selectedIds.includes(id) ? [] : [id]);
    } else if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((i) => i !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  }

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-4">
      <InputGroup>
        <InputGroupAddon align="inline-start">
          <InputGroupText>
            <Search className="size-4" />
          </InputGroupText>
        </InputGroupAddon>
        <InputGroupInput
          className="!text-sm"
          placeholder={t('wizard.atsSelector.search')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />
      </InputGroup>

      {isLoading && (
        <div className="flex flex-col gap-2">
          {[1, 2, 3, 4].map((n) => (
            <Skeleton key={n} className="h-12 w-full" />
          ))}
        </div>
      )}

      {!isLoading && filtered && filtered.length === 0 && (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {t('wizard.atsSelector.noJobs')}
        </p>
      )}

      {!isLoading && paginated && paginated.length > 0 && (
        <>
          <div className="flex flex-1 min-h-0 flex-col gap-2 overflow-y-auto">
            {paginated.map((job) => (
              <label
                key={job.id}
                className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-card p-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              >
                <input
                  type={singleSelect ? 'radio' : 'checkbox'}
                  checked={selectedIds.includes(job.id)}
                  onChange={() => {
                    toggleJob(job.id);
                  }}
                  className="mt-0.5 accent-primary"
                />
                <div className="min-w-0 flex flex-col gap-0.5">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium">{job.title}</span>
                    {job.location && (
                      <span className="text-xs text-muted-foreground">{job.location}</span>
                    )}
                  </div>
                  {job.description ? (
                    <span className="line-clamp-2 text-xs text-muted-foreground">
                      {job.description
                        .replace(/<[^>]*>/g, ' ')
                        .replace(/\s+/g, ' ')
                        .trim()}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground/70 italic">
                      {t('wizard.atsSelector.noDescription')}
                    </span>
                  )}
                </div>
              </label>
            ))}
          </div>

          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    aria-disabled={page === 1}
                    className={page === 1 ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
                {getPageRange(page, totalPages).map((item, i) =>
                  item === 'ellipsis' ? (
                    <PaginationItem key={`ellipsis-${String(i)}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={item}>
                      <PaginationLink
                        isActive={item === page}
                        onClick={() => {
                          setPage(item);
                        }}
                      >
                        {item}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => {
                      setPage((p) => Math.min(totalPages, p + 1));
                    }}
                    aria-disabled={page === totalPages}
                    className={page === totalPages ? 'pointer-events-none opacity-50' : ''}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}

      <div className="flex justify-end">
        <Button disabled={selectedIds.length === 0} onClick={onContinue}>
          {t('wizard.atsSelector.continue', { count: selectedIds.length })}
        </Button>
      </div>
    </div>
  );
}
